import type { Ref } from 'react';
import { Button, Card, Lock } from '@/components/ui';
import { useT } from '@/i18n';
import type { LockedPage as LockedPageDocument } from '@/api/types';

export interface LockedPageProps {
  page: LockedPageDocument;
  /** Only the first locked page in the document gets the unlock CTA — the rest are stubs. */
  showUnlock: boolean;
  onUnlock?: () => void;
  price?: string;
  sectionRef?: Ref<HTMLElement>;
}

/** Sage placeholder bars standing in for the blocks this page will hold once unlocked —
 * decorative only, so hidden from assistive tech. */
const PLACEHOLDER_BAR_COUNT = 6;

export const LockedPage = ({ page, showUnlock, onUnlock, price, sectionRef }: LockedPageProps) => {
  const t = useT();
  const headingId = `report-page-${page.number}-heading`;

  return (
    <section ref={sectionRef} data-page-number={page.number} aria-labelledby={headingId} className="scroll-mt-24">
      <h2 id={headingId} className="text-h2">
        {page.title}
      </h2>
      <p className="mt-1 flex items-center gap-1.5 text-caption text-ink-muted">
        <Lock className="h-4 w-4" aria-hidden="true" />
        {t('report.lockedList')}
      </p>

      <Card tone="sage" padding="lg" className="mt-4 space-y-5">
        <p className="text-body text-ink-soft">{page.description}</p>

        <div aria-hidden="true" className="space-y-3">
          {Array.from({ length: PLACEHOLDER_BAR_COUNT }, (_, i) => (
            <div
              key={i}
              className="h-3 rounded-sm bg-sage-300"
              style={i % 3 === 2 ? { width: '60%' } : undefined}
            />
          ))}
        </div>

        {showUnlock && onUnlock && (
          <Button onClick={onUnlock}>{t('report.unlock', { price: price ?? '' })}</Button>
        )}
      </Card>
    </section>
  );
};
